import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  deleteUser,
  signOut,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { 
  validateRole, 
  normalizeRole, 
  isCmtRole,
  ROLES 
} from '../models/role.js';

const auth = getAuth();

export const getUserData = async (userId) => {
  try {
    // Note: Super admin status is now determined by superAdmin field in user document
    
    const userDoc = await getDoc(doc(db, 'c3_users', userId));
    
    if (!userDoc.exists()) {
      
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (currentUser && currentUser.uid === userId) {
        console.log('User exists in Auth, creating user document');
        
        const userData = {
          uid: userId,
          email: currentUser.email,
          role: ROLES.CMT_LEADER,  // Use constant instead of string
          orgId: 'crisiscompass',
          createdAt: new Date(),
          updatedAt: new Date(),
          cmtIds: [],
          accessibleCmts: []
        };
        
        await setDoc(doc(db, 'c3_users', userId), userData);
        console.log('Created user document:', userData);
        
        return userData;
      }
      
      console.error('User document does not exist and user not found in Auth:', userId);
      throw new Error('User document not found');
    }
    
    const userData = userDoc.data();
    console.log('Retrieved user data directly:', userData);
    
    // Validate role
    if (userData.role) {
      userData.role = validateRole(userData.role);
    }
    
    if (!userData.orgId) {
      console.error('User data missing orgId:', userData);
      throw new Error('User data missing required fields');
    }

    // 🚨 REMOVED: Hardcoded email logic that was interfering with CMT forms
    // No longer auto-setting firstName for specific email addresses

    // Ensure CMT arrays exist and are arrays
    if (!Array.isArray(userData.cmtIds)) {
      userData.cmtIds = [];
      await updateDoc(doc(db, 'c3_users', userId), {
        cmtIds: [],
        updatedAt: new Date()
      });
    }
    if (!Array.isArray(userData.accessibleCmts)) {
      userData.accessibleCmts = [];
      await updateDoc(doc(db, 'c3_users', userId), {
        accessibleCmts: [],
        updatedAt: new Date()
      });
    }

    // Do not auto-assign legacy default CMTs. Membership must come from org's cmts and user assignments.
    
    return userData;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

export const createUser = async (email, password, userData) => {
  try {
    // Validate role if provided
    if (userData.role) {
      userData.role = validateRole(userData.role);
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    if (userData.firstName && userData.lastName) {
      await updateProfile(user, {
        displayName: `${userData.firstName} ${userData.lastName}`
      });
    }

    await setDoc(doc(db, 'c3_users', user.uid), {
      ...userData,
      email,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      displayName: userData.firstName && userData.lastName ? 
        `${userData.firstName} ${userData.lastName}` : email,
      createdAt: new Date(),
      updatedAt: new Date()
    });

    return user;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    // Validate role if being updated
    if (userData.role) {
      userData.role = validateRole(userData.role);
    }

    const userRef = doc(db, 'c3_users', userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUserAccount = async (userId) => {
  try {
    // Delete user document from Firestore
    await deleteDoc(doc(db, 'c3_users', userId));
    
    // Delete user from Firebase Auth
    const user = auth.currentUser;
    if (user) {
      await deleteUser(user);
    }
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const resetUserPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId, profileData) => {
  try {
    const userRef = doc(db, 'c3_users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const updates = {
      ...profileData,
      updatedAt: new Date()
    };

    // If first or last name is updated, update the display name
    if (profileData.firstName || profileData.lastName) {
      const currentData = userDoc.data();
      const firstName = profileData.firstName || currentData.firstName;
      const lastName = profileData.lastName || currentData.lastName;
      updates.displayName = `${firstName} ${lastName}`;
      
      // Update Firebase Auth profile
      const user = auth.currentUser;
      if (user) {
        await updateProfile(user, {
          displayName: updates.displayName
        });
      }
    }

    await updateDoc(userRef, updates);
    return { id: userId, ...updates };
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const updateUserOrgId = async (userId, orgId) => {
  try {
    const userRef = doc(db, 'c3_users', userId);
    await updateDoc(userRef, {
      orgId: orgId,
      updatedAt: new Date()
    });
    console.log(`Updated user ${userId} orgId to ${orgId}`);
  } catch (error) {
    console.error('Error updating user orgId:', error);
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    
    if (!userId) {
      throw new Error('No user ID returned from authentication');
    }
    
    // Use our enhanced getUserData function instead of direct document access
    const userData = await getUserData(userId);
    
    // Update last login timestamp
    const userRef = doc(db, 'c3_users', userId);
    await updateDoc(userRef, {
      lastLogin: new Date(),
      lastLoginAt: new Date(),
      updatedAt: new Date()
    });
    
    // Update userData with the new login timestamp
    userData.lastLogin = new Date();
    userData.lastLoginAt = new Date();
    
    // 🚨 REMOVED: Hardcoded email logic that was interfering with CMT forms
    // No longer auto-setting orgId for specific email addresses

    // 🎯 MODERNIZED: Active CMT is derived from cmtMembers collection.
    
    return { user: userCredential.user, userData };
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

// Alias for createUser to maintain backward compatibility
export const registerUser = createUser;

// Alias for resetUserPassword to maintain backward compatibility
export const resetPassword = resetUserPassword;

export const createAdminUser = async (userId, email) => {
  try {
    await setDoc(doc(db, 'c3_users', userId), {
      email,
      firstName: 'Admin',
      lastName: 'User',
      displayName: 'Admin User',
      role: 'admin',
      tier: 'enterprise_pro',
      createdAt: new Date(),
      updatedAt: new Date(),
      orgName: 'CrisisCompass',
      isAdmin: true,
      orgId: 'crisiscompass'
    });
  } catch (error) {
    console.error('Error creating admin user:', error);
    throw error;
  }
};

export const updateJamesProfile = async () => {
  try {
    const usersRef = collection(db, 'c3_users');
    const q = query(usersRef, where('email', '==', 'james@crisiscompass.com.au'));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.error('No user found with email james@crisiscompass.com.au');
      return;
    }

    const userDoc = querySnapshot.docs[0];
    const userRef = userDoc.ref;

    await updateDoc(userRef, {
      firstName: 'James',
      lastName: 'Neilson',
      displayName: 'James Neilson',
      updatedAt: new Date()
    });

    console.log('Successfully updated user profile for James Neilson');
  } catch (error) {
    console.error('Error updating user profile:', error);
  }
};

export const updateUserCmtMembership = async (userId, cmtId) => {
  try {
    const userRef = doc(db, 'c3_users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const cmtIds = userData.cmtIds || [];
    const accessibleCmts = userData.accessibleCmts || [];

    // Add CMT to both arrays if not already present
    if (!cmtIds.includes(cmtId)) {
      cmtIds.push(cmtId);
    }
    if (!accessibleCmts.includes(cmtId)) {
      accessibleCmts.push(cmtId);
    }

    await updateDoc(userRef, {
      cmtIds,
      accessibleCmts,
      updatedAt: new Date()
    });

    console.log(`Updated CMT membership for user ${userId}:`, { cmtIds, accessibleCmts });
    return { cmtIds, accessibleCmts };
  } catch (error) {
    console.error('Error updating user CMT membership:', error);
    throw error;
  }
};

export const updateCurrentUserLoginTimestamp = async () => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('No user is currently logged in');
    }
    
    const userRef = doc(db, 'c3_users', currentUser.uid);
    await updateDoc(userRef, {
      lastLogin: new Date(),
      lastLoginAt: new Date(),
      updatedAt: new Date()
    });
    
    console.log('Updated login timestamp for current user:', currentUser.uid);
    return true;
  } catch (error) {
    console.error('Error updating login timestamp:', error);
    throw error;
  }
};

export const updateCurrentUserName = async (firstName, lastName) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('No user is currently logged in');
    }
    
    const userRef = doc(db, 'c3_users', currentUser.uid);
    const displayName = `${firstName} ${lastName}`;
    
    await updateDoc(userRef, {
      firstName: firstName,
      lastName: lastName,
      displayName: displayName,
      updatedAt: new Date()
    });
    
    // Also update Firebase Auth profile
    await updateProfile(currentUser, {
      displayName: displayName
    });
    
    console.log('Updated user name:', { firstName, lastName, displayName });
    return { firstName, lastName, displayName };
  } catch (error) {
    console.error('Error updating user name:', error);
    throw error;
  }
}; 